// src/features/dashboard/services/adminDashboardService.ts

import api from '../../../api/axios';

import {
  HISTORY_ENDPOINTS,
  STORE_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import type {
  AdminDashboardData,
  DailySeriesItem,
  DailyStatsItem,
  StoreGameParticipant,
  TodayGameData,
} from '../types/superAdminDashboard.types';


const formatLocalDate = (
  date: Date,
): string => {
  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1,
    ).padStart(
      2,
      '0',
    );

  const day =
    String(
      date.getDate(),
    ).padStart(
      2,
      '0',
    );

  return `${year}-${month}-${day}`;
};


const getLastSevenDaysRange =
  () => {
    const endDate =
      new Date();

    const startDate =
      new Date();

    startDate.setDate(
      endDate.getDate() -
        6,
    );


    return {
      startDate,
      endDate,
    };
  };


const buildDailySeries = (
  stats:
    DailyStatsItem[],
  startDate: Date,
): DailySeriesItem[] => {
  const map =
    new Map<
      string,
      DailyStatsItem
    >(
      stats.map(
        (
          item,
        ) => [
          item.date,
          item,
        ],
      ),
    );


  return Array.from(
    {
      length: 7,
    },
    (
      _,
      index,
    ) => {
      const date =
        new Date(
          startDate,
        );

      date.setDate(
        startDate.getDate() +
          index,
      );


      const dateKey =
        formatLocalDate(
          date,
        );


      const item =
        map.get(
          dateKey,
        );


      return {
        date:
          dateKey.slice(
            5,
          ),

        visits:
          item?.dau ??
          0,

        participants:
          item?.joinCount ??
          0,
      };
    },
  );
};


export const adminDashboardService = {
  /**
   * 대시보드 전체 데이터 조회
   */
  async getDashboard():
    Promise<AdminDashboardData> {
    const {
      startDate,
      endDate,
    } =
      getLastSevenDaysRange();


    const [
      todayVisitorsResponse,
      dailyResponse,
      todayGameResponse,
      storesResponse,
    ] =
      await Promise.all([
        api.get<
          ApiResponse<number>
        >(
          HISTORY_ENDPOINTS
            .SUPERADMIN
            .TODAY_VISITORS,
        ),

        api.get<
          ApiResponse<
            DailyStatsItem[]
          >
        >(
          HISTORY_ENDPOINTS
            .SUPERADMIN
            .DAILY_VISITORS,
          {
            params: {
              startDate:
                formatLocalDate(
                  startDate,
                ),

              endDate:
                formatLocalDate(
                  endDate,
                ),
            },
          },
        ),

        api.get<
          ApiResponse<
            TodayGameData
          >
        >(
          HISTORY_ENDPOINTS
            .SUPERADMIN
            .TODAY_GAME_PARTICIPANTS,
        ),

        api.get<
          ApiResponse<
            StoreListItem[]
          >
        >(
          STORE_ENDPOINTS
            .LIST,
        ),
      ]);


    const todayVisitCount =
      todayVisitorsResponse
        .data
        .data ??
      0;


    const dailyStats =
      dailyResponse
        .data
        .data ??
      [];


    const gameData =
      todayGameResponse
        .data
        .data ?? {
        joinCount: 0,
        gameList: [],
      };


    const stores =
      (
        storesResponse
          .data
          .data ??
        []
      ).filter(
        (
          store,
        ) =>
          store.storeId !==
          0,
      );


    const joinCountMap =
      new Map<
        number,
        number
      >(
        (
          gameData.gameList ??
          []
        ).map(
          (
            game,
          ) => [
            game.storeId,
            game.joinCount,
          ],
        ),
      );


    const storeGameParticipants:
      StoreGameParticipant[] =
      stores.map(
        (
          store,
        ) => ({
          storeId:
            store.storeId,

          storeName:
            store.storeName,

          joinCount:
            joinCountMap.get(
              store.storeId,
            ) ??
            0,
        }),
      );


    return {
      todayVisitCount,

      todayGameParticipantCount:
        gameData.joinCount ??
        0,

      dailySeries:
        buildDailySeries(
          dailyStats,
          startDate,
        ),

      storeGameParticipants,
    };
  },
};